import { XMarkIcon } from '@heroicons/react/16/solid';
import React, { useCallback, useState } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { WorkflowStatus } from '@/services/api/generated/firmsService';

interface TextListFieldProps {
  label: string;
  description?: string | null;
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  status?: WorkflowStatus;
  placeholder?: string;
}

/** Basic email validation regex */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Text list field for managing arrays of strings (e.g., email addresses).
 * Renders values as removable chips/tags with an input to add new items.
 */
export const TextListField: React.FC<TextListFieldProps> = ({
  label,
  description,
  values,
  onChange,
  disabled = false,
  isLoading = false,
  status,
  placeholder = 'Add email address...',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addValue = useCallback(
    (value: string) => {
      const trimmed = value.trim().toLowerCase();
      if (!trimmed) return;

      // Validate email format
      if (!EMAIL_REGEX.test(trimmed)) {
        setError('Please enter a valid email address');
        return;
      }

      // Check for duplicates (case-insensitive)
      if (values.some((v) => v.toLowerCase() === trimmed)) {
        setError('This email is already in the list');
        return;
      }

      setError(null);
      onChange([...values, trimmed]);
      setInputValue('');
    },
    [values, onChange]
  );

  const removeValue = useCallback(
    (index: number) => {
      const newValues = values.filter((_, i) => i !== index);
      onChange(newValues);
    },
    [values, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addValue(inputValue);
      } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
        // Remove last item when backspace is pressed on empty input
        removeValue(values.length - 1);
      }
    },
    [inputValue, values.length, addValue, removeValue]
  );

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addValue(inputValue);
    }
  }, [inputValue, addValue]);

  const isFieldDisabled = disabled || isLoading;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-medium text-zinc-950 dark:text-white">
          {label}
        </div>
        {status === 'coming_soon' && (
          <Badge color="zinc" className="text-[10px] py-0 px-1">
            Not Live
          </Badge>
        )}
      </div>
      {description && (
        <div className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400">
          {description}
        </div>
      )}

      {/* Tags display */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {values.map((value, index) => (
            <span
              key={`${value}-${index}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded"
            >
              {value}
              {!isFieldDisabled && (
                <button
                  type="button"
                  onClick={() => removeValue(index)}
                  className="hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  aria-label={`Remove ${value}`}
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Input field */}
      <div className="space-y-0.5">
        <input
          type="email"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={isFieldDisabled}
          placeholder={placeholder}
          className="w-full px-2 py-1 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        />
        {error && (
          <div className="text-[10px] text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
        <div className="text-[10px] text-zinc-400 dark:text-zinc-500">
          Press Enter to add
        </div>
      </div>
    </div>
  );
};
